import { useCallback, useEffect, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { formatChf } from '@raporto/shared'
import { OfficeHead } from '../ui/OfficeShell.js'
import { ActionButton } from '../ui/ActionButton.js'
import { notify, notifyError } from '../ui/Toasts.js'
import { uuidv7 } from '../net/ids.js'
import { Belegtabelle, type Vorgabe } from './OfficeCustomers.js'
import {
  api,
  type BueroProjekt,
  type ProjektBlatt,
  type ProjektFilter,
  type TenantUser
} from '../net/apiClient.js'

/**
 * Baustellen im Büro.
 *
 * Die Spalte, um die es hier geht, heisst «unverrechnet»: Rapportzeilen, die
 * geschrieben, aber auf keiner Rechnung sind. Das ist die Zahl, wegen der ein
 * Betrieb Geld verliert – nicht, weil jemand falsch rechnet, sondern weil
 * eine Baustelle im Sommer fertig wurde und im November niemand mehr daran
 * denkt.
 *
 * Deshalb ist «Unverrechnet» ein eigener Filter und nicht nur eine Spalte:
 * die Frage «wo liegt noch Arbeit herum» soll ein Klick sein.
 */

const datum = (iso: string | null): string =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('de-CH') : '–'

const FILTER: Array<{ id: ProjektFilter; label: string }> = [
  { id: 'offen', label: 'Offen' },
  { id: 'unverrechnet', label: 'Unverrechnet' },
  { id: 'abgeschlossen', label: 'Abgeschlossen' },
  { id: 'archiviert', label: 'Archiviert' },
  { id: 'alle', label: 'Alle' }
]

const ZUSTAND: Record<string, string> = {
  offen: 'o-badge-accent',
  abgeschlossen: 'o-badge-ok',
  archiviert: 'o-badge-neutral'
}

export function OfficeProjects({
  aufBeleg,
  aufSchnellstart,
  aufRapporte,
  oeffneProjekt,
  onProjektGeoeffnet
}: {
  session: SessionUser
  aufBeleg?: (id: string) => void
  aufSchnellstart?: (art: 'rechnung' | 'offerte', vorgabe: Vorgabe) => void
  aufRapporte?: () => void
  /** Aus dem Kundenblatt angesprungen. */
  oeffneProjekt?: string | null
  onProjektGeoeffnet?: () => void
}): JSX.Element {
  const [filter, setFilter] = useState<ProjektFilter>('offen')
  const [suche, setSuche] = useState('')
  const [items, setItems] = useState<BueroProjekt[]>([])
  const [zaehler, setZaehler] = useState<Record<string, number>>({})
  const [summe, setSumme] = useState<{ unverrechnet: number; verrechnetRp: number } | null>(null)
  const [laedt, setLaedt] = useState(true)
  const [offen, setOffen] = useState<string | null>(null)

  const laden = useCallback(async () => {
    setLaedt(true)
    try {
      const r = await api.officeProjects(filter, suche)
      setItems(r.items)
      setZaehler(r.zaehler as unknown as Record<string, number>)
      setSumme(r.summe as { unverrechnet: number; verrechnetRp: number })
    } catch (err) {
      notifyError(err)
    } finally {
      setLaedt(false)
    }
  }, [filter, suche])

  useEffect(() => {
    void laden()
  }, [laden])

  // Ein Sprung aus dem Kundenblatt öffnet direkt die Baustelle. Ohne das
  // landet man in einer Liste und sucht den Eintrag, den man angetippt hat.
  useEffect(() => {
    if (!oeffneProjekt) return
    setOffen(oeffneProjekt)
    onProjektGeoeffnet?.()
  }, [oeffneProjekt, onProjektGeoeffnet])

  if (offen) {
    return (
      <Baustellenblatt
        id={offen}
        aufBeleg={aufBeleg}
        aufSchnellstart={aufSchnellstart}
        aufRapporte={aufRapporte}
        zurueck={() => {
          setOffen(null)
          void laden()
        }}
      />
    )
  }

  return (
    <>
      <OfficeHead
        titel="Projekte"
        unter={
          summe
            ? summe.unverrechnet > 0
              ? `${summe.unverrechnet} Rapportzeilen sind noch nicht verrechnet`
              : 'Alle Rapportzeilen sind verrechnet'
            : 'Wird geladen …'
        }
      />

      <div className="o-card">
        <div className="o-pad">
          <div className="o-suchzeile">
            <div className="o-filter" id="projektfilter">
              {FILTER.map((f) => (
                <button
                  key={f.id}
                  id={`pfilter-${f.id}`}
                  className={f.id === filter ? 'o-chip aktiv' : 'o-chip'}
                  aria-pressed={f.id === filter}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                  <span className="o-chip-zahl">{zaehler[f.id] ?? 0}</span>
                </button>
              ))}
            </div>
            <div className="o-field o-feldsuche">
              <label htmlFor="projekt-suche">Suche</label>
              <input
                id="projekt-suche"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                placeholder="Projekt, Kunde, Ort oder Strasse"
                autoComplete="off"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="o-card" id="projektliste">
        <div className="o-card-head">
          <h3>
            {FILTER.find((f) => f.id === filter)?.label}{' '}
            <span className="o-badge o-badge-accent">{items.length}</span>
          </h3>
          <span className="o-soft">
            Unverrechnet heisst: die Rapportzeile ist auf keiner Rechnung.
          </span>
        </div>

        {laedt ? (
          <div className="o-leer">Wird geladen …</div>
        ) : items.length === 0 ? (
          <div className="o-leer" id="projekte-leer">
            {suche.trim() ? (
              <>
                <strong>Kein Treffer.</strong>
                Gesucht wird über Projekt, Kunde, Ort und Strasse.
              </>
            ) : filter === 'unverrechnet' ? (
              <>
                <strong>Nichts liegen geblieben.</strong>
                Jede Rapportzeile steht auf einer Rechnung.
              </>
            ) : (
              <>
                <strong>Nichts in dieser Auswahl.</strong>
                Projekte entstehen bei der Rapporterfassung.
              </>
            )}
          </div>
        ) : (
          <div className="o-tabelle">
            <table className="o-data">
              <thead>
                <tr>
                  <th>Projekt</th>
                  <th style={{ width: 210 }}>Kunde</th>
                  <th style={{ width: 180 }}>Ort</th>
                  <th style={{ width: 130 }}>Zustand</th>
                  <th className="o-num" style={{ width: 100 }}>
                    Rapporte
                  </th>
                  <th className="o-num" style={{ width: 130 }}>
                    Unverrechnet
                  </th>
                  <th className="o-num" style={{ width: 125 }}>
                    Verrechnet
                  </th>
                  <th style={{ width: 130 }}>Letzte Arbeit</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr
                    key={p.id}
                    id={`projekt-${p.id}`}
                    className={p.status === 'archiviert' ? 'klickbar inaktiv' : 'klickbar'}
                    onClick={() => setOffen(p.id)}
                  >
                    <td>{p.name}</td>
                    <td className="o-soft">{p.customerName}</td>
                    <td className="o-soft">{[p.zip, p.city].filter(Boolean).join(' ') || '–'}</td>
                    <td>
                      <span className={`o-badge ${ZUSTAND[p.status] ?? 'o-badge-neutral'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="o-num">{p.rapporte || '–'}</td>
                    <td className="o-num">
                      {p.unverrechnet > 0 ? (
                        <span className="o-badge o-badge-warn">{p.unverrechnet}</span>
                      ) : (
                        '–'
                      )}
                    </td>
                    <td className="o-num">
                      {p.verrechnetRp > 0 ? formatChf(p.verrechnetRp) : '–'}
                    </td>
                    <td className="o-soft">{datum(p.letzteArbeit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------

function Baustellenblatt({
  id,
  zurueck,
  aufBeleg,
  aufSchnellstart,
  aufRapporte
}: {
  id: string
  zurueck: () => void
  aufBeleg?: (id: string) => void
  aufSchnellstart?: (art: 'rechnung' | 'offerte', vorgabe: Vorgabe) => void
  aufRapporte?: () => void
}): JSX.Element {
  const [daten, setDaten] = useState<ProjektBlatt | null>(null)

  const laden = useCallback(async () => {
    try {
      setDaten(await api.officeProject(id))
    } catch (err) {
      notifyError(err)
    }
  }, [id])

  useEffect(() => {
    void laden()
  }, [laden])

  if (!daten) {
    return (
      <>
        <button className="o-zurueck" id="btn-projekt-zurueck" onClick={zurueck}>
          ← Projekte
        </button>
        <div className="o-card">
          <div className="o-leer">Wird geladen …</div>
        </div>
      </>
    )
  }

  const p = daten.projekt
  const offeneZeilen = daten.rapporte.reduce((s, r) => s + r.unverrechnet, 0)
  const vorgabe: Vorgabe = {
    customerId: p.customerId,
    customerName: p.customerName,
    projectId: p.id,
    projectName: p.name,
    objectStreet: p.street,
    objectZip: p.zip,
    objectCity: p.city
  }

  return (
    <>
      <button className="o-zurueck" id="btn-projekt-zurueck" onClick={zurueck}>
        ← Projekte
      </button>

      <OfficeHead
        titel={p.name}
        unter={`${p.customerName}${
          [p.street, [p.zip, p.city].filter(Boolean).join(' ')].filter(Boolean).length
            ? ` · ${[p.street, [p.zip, p.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}`
            : ''
        }`}
      >
        <button
          className="o-btn o-btn-primary"
          id="btn-projekt-rechnung"
          onClick={() => aufSchnellstart?.('rechnung', vorgabe)}
        >
          Rechnung schreiben
        </button>
        <button
          className="o-btn"
          id="btn-projekt-offerte"
          onClick={() => aufSchnellstart?.('offerte', vorgabe)}
        >
          Offerte schreiben
        </button>
      </OfficeHead>

      {/*
        Der Hinweis steht oben und nicht unten bei den Rapporten: er ist der
        Grund, warum jemand dieses Projekt aufgeschlagen hat.
      */}
      {offeneZeilen > 0 && (
        <div className="o-band" id="projekt-unverrechnet">
          <span>
            <strong>
              {offeneZeilen === 1
                ? 'Eine Rapportzeile ist noch nicht verrechnet.'
                : `${offeneZeilen} Rapportzeilen sind noch nicht verrechnet.`}
            </strong>{' '}
            Verrechnet wird im Rapportbildschirm – dort lassen sich die Zeilen auswählen.
          </span>
          <button className="o-btn" id="btn-projekt-rapporte" onClick={() => aufRapporte?.()}>
            Zu den Rapporten
          </button>
        </div>
      )}

      <ProjektAbschluss projekt={p} offeneZeilen={offeneZeilen} aufGeaendert={laden} />

      <Zuweisung projektId={p.id} />

      <div className="o-card" id="projekt-rapporte-liste">
        <div className="o-card-head">
          <h3>
            Rapporte <span className="o-badge o-badge-accent">{daten.rapporte.length}</span>
          </h3>
          <span className="o-soft">{p.status}</span>
        </div>
        {daten.rapporte.length === 0 ? (
          <div className="o-leer">
            <strong>Noch kein Rapport.</strong>
            Rapporte entstehen auf dem Platz – am Handy des Monteurs.
          </div>
        ) : (
          <div className="o-tabelle">
            <table className="o-data">
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Datum</th>
                  <th>Monteur</th>
                  <th style={{ width: 110 }}>Art</th>
                  <th className="o-num" style={{ width: 100 }}>
                    Zeilen
                  </th>
                  <th className="o-num" style={{ width: 130 }}>
                    Unverrechnet
                  </th>
                  <th style={{ width: 130 }}>Unterschrift</th>
                </tr>
              </thead>
              <tbody>
                {daten.rapporte.map((r) => (
                  <tr key={r.id} id={`prapport-${r.id}`}>
                    <td>{datum(r.workDate)}</td>
                    <td>{r.userName}</td>
                    <td className="o-soft">{r.isRegie ? 'Regie' : 'Auftrag'}</td>
                    <td className="o-num">{r.zeilen}</td>
                    <td className="o-num">
                      {r.unverrechnet > 0 ? (
                        <span className="o-badge o-badge-warn">{r.unverrechnet}</span>
                      ) : (
                        '–'
                      )}
                    </td>
                    <td>
                      {r.signiert ? (
                        <span className="o-badge o-badge-ok">unterschrieben</span>
                      ) : (
                        <span className="o-soft">–</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Belegtabelle
        titel="Belege zu diesem Projekt"
        belege={daten.belege}
        aufBeleg={aufBeleg}
        leer="Zu diesem Projekt gibt es noch keinen Beleg."
      />
    </>
  )
}

/**
 * Ein Projekt abschliessen – oder wieder öffnen.
 *
 * Den Status «abgeschlossen» gab es längst: im Server, in der Liste als
 * Filter und als Abzeichen. Nur setzen liess er sich im Büro nirgends. Hier
 * steht jetzt die eine Aktion dafür, mit Rückfrage – und mit dem Hinweis, wenn
 * noch Rapportzeilen unverrechnet sind, denn das ist der häufigste Grund, ein
 * Projekt doch nicht abzuschliessen. Gesperrt wird dadurch nichts: Rapporte,
 * Belege und Zuweisungen bleiben, und «Wieder öffnen» macht es rückgängig.
 * Archivieren bleibt ein eigener Schritt.
 */
function ProjektAbschluss({
  projekt,
  offeneZeilen,
  aufGeaendert
}: {
  projekt: ProjektBlatt['projekt']
  offeneZeilen: number
  aufGeaendert: () => Promise<void>
}): JSX.Element | null {
  const [frage, setFrage] = useState(false)
  if (projekt.status === 'archiviert') return null
  const abgeschlossen = projekt.status === 'abgeschlossen'

  return (
    <div className="o-card" id="projekt-abschluss">
      <div className="o-card-head">
        <h3>
          Status{' '}
          <span className={`o-badge ${ZUSTAND[projekt.status] ?? 'o-badge-neutral'}`}>
            {abgeschlossen ? 'abgeschlossen' : 'offen'}
          </span>
        </h3>
        {!frage && (
          <button
            type="button"
            className="o-btn"
            id={abgeschlossen ? 'btn-projekt-oeffnen' : 'btn-projekt-abschliessen'}
            onClick={() => setFrage(true)}
          >
            {abgeschlossen ? 'Wieder öffnen' : 'Projekt abschliessen'}
          </button>
        )}
      </div>
      {frage && (
        <div className="o-dialog-schritte" id="projekt-abschluss-frage">
          <p>
            {abgeschlossen
              ? 'Das Projekt wird wieder als offen geführt.'
              : 'Das Projekt wird als abgeschlossen geführt. Rapporte, Belege und Zuweisungen bleiben erhalten; «Wieder öffnen» macht es rückgängig.'}
          </p>
          {!abgeschlossen && offeneZeilen > 0 && (
            <p className="o-soft" id="projekt-abschluss-warnung">
              {offeneZeilen === 1
                ? 'Achtung: Eine Rapportzeile ist noch nicht verrechnet.'
                : `Achtung: ${offeneZeilen} Rapportzeilen sind noch nicht verrechnet.`}
            </p>
          )}
          <div className="rowactions">
            <ActionButton
              id="btn-projekt-status-bestaetigen"
              actionClass="B"
              labels={
                abgeschlossen
                  ? { idle: 'Wieder öffnen', busy: 'Speichert …', success: 'Geöffnet' }
                  : { idle: 'Abschliessen', busy: 'Speichert …', success: 'Abgeschlossen' }
              }
              onAction={async () => {
                await api.projektStatusSetzen(
                  projekt,
                  abgeschlossen ? 'offen' : 'abgeschlossen',
                  uuidv7()
                )
                setFrage(false)
                notify.info(abgeschlossen ? 'Projekt wieder geöffnet.' : 'Projekt abgeschlossen.')
                await aufGeaendert()
              }}
              onError={(err) => {
                notifyError(err)
                void aufGeaendert()
              }}
            />
            <ActionButton
              variant="quiet"
              labels={{ idle: 'Abbrechen', busy: '…' }}
              onAction={async () => setFrage(false)}
            />
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Wer dieses Projekt sieht.
 *
 * ---------------------------------------------------------------------------
 * Warum diese Karte hier neu entsteht
 * ---------------------------------------------------------------------------
 *
 * Die Zuweisung entscheidet über alles, was ein Monteur überhaupt tun kann:
 * `GET /projects` gibt ihm nur Projekte, die in `project_members` stehen
 * (routes/projects.ts), und ohne Projekt kann er keinen Rapport erfassen –
 * die Startseite sagt ihm dann «Dir ist zurzeit kein Projekt zugewiesen».
 *
 * Die Maske dafür gab es, aber an der falschen Stelle: in `Projects.tsx`,
 * hinter `isSuperuser` versteckt – und `Projects.tsx` bekommt in App.tsx
 * ausgerechnet **nur der Monteur** zu sehen. Der Superuser landet in
 * `OfficeProjects`, und dort war die Zuweisung nicht vorhanden. Damit war ein
 * frisch eingeladener Mitarbeiter über die Oberfläche nicht ans Arbeiten zu
 * bringen; genau das ist in der Vorführung passiert.
 *
 * Die Karte lädt bewusst erst beim Öffnen eines Projekts und nicht mit der
 * Liste: die Personenliste eines Betriebs ändert sich selten, und sie gehört
 * nicht in eine Übersicht, in der man Zahlen sucht.
 */
function Zuweisung({ projektId }: { projektId: string }): JSX.Element {
  const [leute, setLeute] = useState<TenantUser[]>([])
  const [gewaehlt, setGewaehlt] = useState<string[]>([])
  const [geladen, setGeladen] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    let abgebrochen = false
    void (async () => {
      try {
        const [alle, projekt] = await Promise.all([
          api.listUsers(),
          api.projektMitglieder(projektId)
        ])
        if (abgebrochen) return
        setLeute(alle.users)
        setGewaehlt(projekt.members.map((m) => m.userId))
        setGeladen(true)
      } catch (err) {
        if (abgebrochen) return
        /*
          Sichtbar scheitern.

          Ein stilles `catch` liesse die Karte für immer bei «Wird geladen …»
          stehen, und niemand wüsste, ob der Betrieb keine Mitarbeiter hat oder
          ob die Abfrage fehlschlug.
        */
        setFehler(err instanceof Error ? err.message : 'Die Personenliste liess sich nicht laden.')
        setGeladen(true)
      }
    })()
    return () => {
      abgebrochen = true
    }
  }, [projektId])

  const monteure = leute.filter((u) => u.role === 'worker' && !u.deaktiviert)

  return (
    <div className="o-card" id="projekt-zuweisung">
      <div className="o-card-head">
        <h3>Zugewiesene Personen</h3>
        <span className="o-soft">{gewaehlt.length} zugewiesen</span>
      </div>

      <p className="o-soft">
        Nur zugewiesene Personen sehen dieses Projekt auf ihrem Gerät und können dazu Rapporte
        erfassen. Geprüft wird das auf dem Server.
      </p>

      {!geladen ? (
        <p className="o-soft">Wird geladen …</p>
      ) : fehler ? (
        <p className="o-soft" role="alert">
          {fehler}
        </p>
      ) : monteure.length === 0 ? (
        <div className="o-leer">
          <strong>Noch keine Mitarbeitenden im Betrieb.</strong>
          Unter «Team» lässt sich jemand einladen; danach erscheint er hier.
        </div>
      ) : (
        <>
          <ul className="checks">
            {monteure.map((u) => (
              <li key={u.userId}>
                <label>
                  <input
                    autoComplete="off"
                    type="checkbox"
                    id={`zuweisen-${u.userId}`}
                    checked={gewaehlt.includes(u.userId)}
                    onChange={(e) =>
                      setGewaehlt((vorher) =>
                        e.target.checked
                          ? [...vorher, u.userId]
                          : vorher.filter((x) => x !== u.userId)
                      )
                    }
                  />
                  {u.name}
                </label>
              </li>
            ))}
          </ul>

          <ActionButton
            id="btn-zuweisung-speichern"
            variant="primary"
            actionClass="B"
            labels={{ idle: 'Zuweisung speichern', busy: 'Speichert …', success: 'Gespeichert' }}
            onAction={async () => {
              await api.projektMitgliederSetzen(projektId, gewaehlt, uuidv7())
              notify.ok('Die Zuweisung ist gespeichert.')
            }}
            onError={(err) => notifyError(err)}
          />
        </>
      )}
    </div>
  )
}
